import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getPortfolioComplianceDates } from 'reducers/complianceDatesPortfolioReducer';
import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { concatAllComplianceDates } from 'utils/complianceDatesUtils';

import Spinner from 'components/Spinner';
import { ReactComponent as ChevronLeftIcon } from 'assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron-right.svg';

import './PortfolioCalendar.css';

const MONTH_NAMES = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getEventDateKey(dateInput) {
	if (!dateInput) return null;
	if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateInput)) {
		return dateInput.slice(0, 10);
	}
	const d = new Date(dateInput);
	if (isNaN(d.getTime())) return null;
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

function formatDisplayDate(dateInput) {
	if (!dateInput) return '-';
	const d = new Date(dateInput);
	if (isNaN(d.getTime())) return String(dateInput);
	const m = d.getMonth() + 1;
	const day = d.getDate();
	const yy = String(d.getFullYear()).slice(-2);
	return `${m}/${day}/${yy}`;
}

function getDaysForMonth(year, month) {
	const firstDayIndex = new Date(year, month, 1).getDay();
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const daysInPrevMonth = new Date(year, month, 0).getDate();

	const days = [];

	// Leading days from previous month
	for (let i = firstDayIndex - 1; i >= 0; i--) {
		const d = daysInPrevMonth - i;
		const dateObj = new Date(year, month - 1, d);
		days.push({
			dayNumber: d,
			date: dateObj,
			dateKey: getEventDateKey(dateObj),
			isCurrentMonth: false,
		});
	}

	// Days of current month
	for (let d = 1; d <= daysInMonth; d++) {
		const dateObj = new Date(year, month, d);
		days.push({
			dayNumber: d,
			date: dateObj,
			dateKey: getEventDateKey(dateObj),
			isCurrentMonth: true,
		});
	}

	// Trailing days to complete multiple of 7
	const remaining = (7 - (days.length % 7)) % 7;
	for (let d = 1; d <= remaining; d++) {
		const dateObj = new Date(year, month + 1, d);
		days.push({
			dayNumber: d,
			date: dateObj,
			dateKey: getEventDateKey(dateObj),
			isCurrentMonth: false,
		});
	}

	return days;
}

const PortfolioCalendar = () => {
	const dispatch = useDispatch();

	const { isLoading, userId, complianceDates } = useSelector((state) => ({
		isLoading: state.complianceDatesPortfolio.isLoading,
		userId: state.user.userData?.id,
		complianceDates: state.complianceDatesPortfolio.data,
	}));

	useEffect(() => {
		if (userId) {
			dispatch(getPortfolioComplianceDates());
		}
	}, [userId, dispatch]);

	const complianceEvents = useMemo(() => {
		if (complianceDates) {
			return concatAllComplianceDates(complianceDates);
		}
		return [];
	}, [complianceDates]);

	// Set of dateKeys that have events
	const eventDateKeySet = useMemo(() => {
		const set = new Set();
		complianceEvents.forEach((ev) => {
			const k = getEventDateKey(ev.date);
			if (k) set.add(k);
		});
		return set;
	}, [complianceEvents]);

	// Initialize to current date (or first event date if available in future)
	const [activeDate, setActiveDate] = useState(() => new Date());
	const [selectedDateKey, setSelectedDateKey] = useState(null);

	const currentYear = activeDate.getFullYear();
	const currentMonth = activeDate.getMonth();

	const calendarDays = useMemo(() => {
		return getDaysForMonth(currentYear, currentMonth);
	}, [currentYear, currentMonth]);

	const handlePrevMonth = () => {
		setActiveDate(new Date(currentYear, currentMonth - 1, 1));
	};

	const handleNextMonth = () => {
		setActiveDate(new Date(currentYear, currentMonth + 1, 1));
	};

	const handleToday = () => {
		const now = new Date();
		setActiveDate(now);
		setSelectedDateKey(getEventDateKey(now));
	};

	const handleDayClick = (dayObj) => {
		if (selectedDateKey === dayObj.dateKey) {
			setSelectedDateKey(null);
		} else {
			setSelectedDateKey(dayObj.dateKey);
		}
	};

	const onEventClick = (event) => {
		dispatch(
			openModal(MODAL_TYPES.CALENDAR_EVENT, {
				eventTitle: event.title || event.description,
			})
		);
	};

	// Events to display on the right
	const displayedEvents = useMemo(() => {
		if (selectedDateKey) {
			return complianceEvents.filter(
				(e) => getEventDateKey(e.date) === selectedDateKey
			);
		}
		// Otherwise show events for the active month or all upcoming events
		const monthPrefix = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
		const monthEvents = complianceEvents.filter((e) => {
			const k = getEventDateKey(e.date);
			return k && k.startsWith(monthPrefix);
		});

		if (monthEvents.length > 0) {
			return monthEvents;
		}

		// Fallback to all compliance events sorted by date
		return [...complianceEvents].sort((a, b) => {
			return (a.date || '').localeCompare(b.date || '');
		});
	}, [complianceEvents, selectedDateKey, currentYear, currentMonth]);

	if (isLoading) {
		return (
			<div className="portfolio-calendar-card tw-flex tw-justify-center tw-py-12">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="portfolio-calendar-card tw-border tw-border-solid tw-border-border-01 tw-rounded-[8px]" style={{ border: '1px solid #E3E3E3' }}>
			{/* Left Column: Mini Calendar */}
			<div className="portfolio-calendar-mini">
				{/* Calendar Header */}
				<div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
					<h3 className="tw-text-base tw-font-bold tw-text-text-primary tw-m-0">
						{MONTH_NAMES[currentMonth]} {currentYear}
					</h3>
					<div className="tw-flex tw-items-center tw-gap-1.5">
						<button
							type="button"
							onClick={handleToday}
							className="tw-rounded-[4px] tw-border tw-border-border-01 tw-bg-white tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-text-primary hover:tw-bg-surface-layer-02 tw-transition-colors"
						>
							Today
						</button>
						<button
							type="button"
							onClick={handlePrevMonth}
							aria-label="Previous month"
							className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-[4px] tw-text-text-secondary hover:tw-bg-surface-layer-02 hover:tw-text-text-primary tw-transition-colors"
						>
							<ChevronLeftIcon className="tw-h-3.5 tw-w-3.5" />
						</button>
						<button
							type="button"
							onClick={handleNextMonth}
							aria-label="Next month"
							className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-[4px] tw-text-text-secondary hover:tw-bg-surface-layer-02 hover:tw-text-text-primary tw-transition-colors"
						>
							<ChevronRightIcon className="tw-h-3.5 tw-w-3.5" />
						</button>
					</div>
				</div>

				{/* Weekday Names */}
				<div className="tw-grid tw-grid-cols-7 tw-text-center tw-text-xs tw-font-semibold tw-text-text-secondary tw-py-1.5">
					{WEEKDAY_NAMES.map((name) => (
						<div key={name}>{name}</div>
					))}
				</div>

				{/* Days Grid */}
				<div className="tw-grid tw-grid-cols-7 tw-border-t tw-border-l tw-border-[#F0F0F0]">
					{calendarDays.map((dayObj, index) => {
						const hasEvents = eventDateKeySet.has(dayObj.dateKey);
						const isSelected = selectedDateKey === dayObj.dateKey;

						return (
							<div
								key={index}
								onClick={() => handleDayClick(dayObj)}
								className={[
									'tw-relative tw-h-9 tw-flex tw-items-center tw-justify-center tw-border-r tw-border-b tw-border-[#E5E5E5] tw-cursor-pointer tw-text-xs tw-transition-colors',
									!dayObj.isCurrentMonth
										? 'tw-text-[#BFBFBF]'
										: isSelected
										? 'tw-bg-[#EBEBEB] tw-font-bold tw-text-text-primary'
										: 'tw-text-text-primary hover:tw-bg-[#EDEDED]',
								].join(' ')}
							>
								<span>{dayObj.dayNumber}</span>
								{hasEvents && (
									<span
										className="tw-absolute tw-top-1.5 tw-right-1.5 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#FF3B30]"
										title="Compliance event"
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Right Column: Events / Hearings List */}
			<div className="portfolio-calendar-events">
				{displayedEvents && displayedEvents.length > 0 ? (
					<div className="tw-flex tw-flex-col">
						{displayedEvents.map((event, idx) => (
							<div
								key={idx}
								onClick={() => onEventClick(event)}
								className="portfolio-calendar-event-item"
							>
								<span className="tw-font-bold tw-text-xs tw-text-text-primary tw-w-24 tw-shrink-0">
									{formatDisplayDate(event.date)}
								</span>
								<span className="tw-text-xs tw-text-text-primary tw-truncate">
									{event.title || event.description || 'Compliance Event'}
								</span>
							</div>
						))}
					</div>
				) : (
					<div className="tw-py-12 tw-text-center tw-text-xs tw-text-text-secondary">
						No hearings or compliance events scheduled for this date.
					</div>
				)}
			</div>
		</div>
	);
};

export default PortfolioCalendar;
